<template>
  <div class="pa-6">
    <v-card outlined flat>
      <v-card-text class="pa-4">
        <v-checkbox
          hide-details
          class="mt-0"
          :label="$t('profile.mailSender.enable')"
          v-model="form.isEnabled"
        />
        <v-expand-transition>
          <div v-if="form.isEnabled" class="mt-4">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.senderAddress"
                  :label="$t('profile.mailSender.senderAddress')"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.senderName"
                  :label="$t('profile.mailSender.senderName')"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.requiresAuthentication"
                  :items="authenticationOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('profile.mailSender.authentication')"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6" v-if="form.requiresAuthentication">
                <v-text-field
                  v-model="form.login"
                  :label="$t('profile.mailSender.login')"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6" v-if="form.requiresAuthentication">
                <v-text-field
                  v-model="form.password"
                  :label="$t('profile.mailSender.password')"
                  :placeholder="form.hasPassword ? $t('profile.mailSender.passwordUnchanged') : ''"
                  type="password"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.smtpServer"
                  :label="$t('profile.mailSender.smtpServer')"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.smtpPort"
                  :label="$t('profile.mailSender.smtpPort')"
                  type="number"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.encryption"
                  :items="encryptionOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('profile.mailSender.encryption')"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.subject"
                  :label="$t('profile.mailSender.subject')"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
        <v-alert
          v-if="testError"
          type="error"
          dense
          outlined
          class="mt-4 mb-0"
        >
          {{ testError }}
        </v-alert>
        <div class="mt-4 d-flex flex-wrap">
          <v-btn color="primary" class="mr-3 mb-2" :loading="saving" @click="save">
            {{ $t('profile.mailSender.save') }}
          </v-btn>
          <v-btn
            color="primary"
            outlined
            class="mb-2"
            :loading="sendingTest"
            :disabled="!form.isEnabled"
            @click="sendTest"
          >
            {{ $t('profile.mailSender.sendTest') }}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Vue from 'vue'
import Service from '@/services'

const EncryptionType = {
  None: 0,
  Ssl: 1,
  StartTls: 2
}

export default {
  name: 'MailSenderConfig',
  data () {
    return {
      saving: false,
      sendingTest: false,
      testError: '',
      form: {
        isEnabled: false,
        senderAddress: '',
        senderName: '',
        login: '',
        password: '',
        hasPassword: false,
        smtpServer: '',
        smtpPort: 587,
        encryption: EncryptionType.StartTls,
        requiresAuthentication: true,
        subject: ''
      }
    }
  },
  computed: {
    encryptionOptions () {
      return [
        { value: EncryptionType.Ssl, text: this.$t('profile.mailSender.encryptionSsl') },
        { value: EncryptionType.StartTls, text: this.$t('profile.mailSender.encryptionStartTls') },
        { value: EncryptionType.None, text: this.$t('profile.mailSender.encryptionNone') }
      ]
    },
    authenticationOptions () {
      return [
        { value: true, text: this.$t('common.yes') },
        { value: false, text: this.$t('common.no') }
      ]
    }
  },
  async created () {
    await this.loadConfig()
  },
  methods: {
    async loadConfig () {
      try {
        const { data } = await Service.get('/User/GetMailSenderConfig')
        this.form = {
          ...this.form,
          ...data,
          password: ''
        }
      } catch (e) {}
    },
    buildPayload () {
      const payload = {
        isEnabled: this.form.isEnabled,
        senderAddress: this.form.senderAddress,
        senderName: this.form.senderName,
        login: this.form.login,
        smtpServer: this.form.smtpServer,
        smtpPort: this.form.smtpPort,
        encryption: this.form.encryption,
        requiresAuthentication: this.form.requiresAuthentication,
        subject: this.form.subject
      }
      if (this.form.password) {
        payload.password = this.form.password
      }
      return payload
    },
    async save () {
      this.saving = true
      try {
        await Service.put('/User/UpdateMailSenderConfig', this.buildPayload())
        await this.loadConfig()
        await this.$store.dispatch(
          'setSuccessNotification',
          this.$t('profile.mailSender.saved')
        )
      } catch (e) {}
      this.saving = false
    },
    async sendTest () {
      this.testError = ''
      this.sendingTest = true
      try {
        await Vue.$http.post('/User/SendTestMailSenderConfig', this.buildPayload())
        await this.$store.dispatch(
          'setSuccessNotification',
          this.$t('profile.mailSender.testSent')
        )
      } catch (e) {
        const title = e.response?.data?.Title
        this.testError = title || this.$t('profile.mailSender.testErrorUnknown')
      }
      this.sendingTest = false
    }
  }
}
</script>
